/**
 * Tool Test Runner
 *
 * End-to-end tests for backend AI tools. Each test set sends natural language
 * prompts to the AI via a real chat session using the user's current account
 * and selected persona. Tests verify that the AI triggers the expected tools
 * and that cloud_tool_result / text responses come back correctly.
 *
 * When running multiple test sets, they all share a single chat session to
 * better simulate real-life conversation flow where the user performs many
 * different operations within one session.
 *
 * Console logging shows each prompt sent and the AI's response for debugging.
 */

import type { ChatEvent, CloudToolResult } from '../types'
import { startChat, stopChat, sendMessage } from './api'

// ── Types ──────────────────────────────────────────────────────

export interface ToolTestStep {
  prompt: string
  /** Expected tool(s) to be triggered. If empty, just expects a text response. */
  expectedTools?: string[]
  /** Expected entity action(s) from cloud_tool_result events */
  expectedActions?: string[]
  /** Validation function run on collected response */
  validate?: (result: StepResult) => string | null // null = pass, string = error message
}

export interface StepResult {
  responseText: string
  toolResults: CloudToolResult[]
  allEvents: ChatEvent[]
}

export interface ToolTestResult {
  name: string
  passed: boolean
  steps: {
    prompt: string
    responseText: string
    toolResults: CloudToolResult[]
    passed: boolean
    error?: string
    duration: number
  }[]
  duration: number
  error?: string
}

export type ToolTestProgressCallback = (
  setName: string,
  stepIndex: number,
  totalSteps: number,
  status: 'running' | 'passed' | 'failed',
  detail?: string,
) => void

// ── Test Set Definitions ───────────────────────────────────────

export interface ToolTestSet {
  name: string
  description: string
  steps: ToolTestStep[]
}

function taskToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Task Tools',
    description: 'Tests create_task, list_todays_tasks, find_task, update_task, complete_task',
    steps: [
      {
        prompt: `Create a new task with the title "Tool Test Task ${tag}" with high priority, due today. Do not ask for confirmation, just create it.`,
        expectedTools: ['create_task'],
        expectedActions: ['created'],
        validate: (r) => {
          const created = r.toolResults.find(t => t.action === 'created' && t.entity_type === 'task')
          return created ? null : 'No task created event received'
        },
      },
      {
        prompt: 'List my tasks for today.',
        expectedTools: ['list_todays_tasks'],
      },
      {
        prompt: `Find the task called "Tool Test Task ${tag}".`,
        expectedTools: ['find_task'],
        validate: (r) => r.responseText.length > 0 ? null : 'No response text',
      },
      {
        prompt: `Update the task "Tool Test Task ${tag}" - change its description to "Updated by tool test" and set priority to low.`,
        expectedTools: ['update_task'],
        expectedActions: ['updated'],
      },
      {
        prompt: `Mark the task "Tool Test Task ${tag}" as completed.`,
        expectedTools: ['complete_task'],
        expectedActions: ['completed'],
      },
    ],
  }
}

function subtaskToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Subtask Tools',
    description: 'Tests create_subtasks, get_subtasks, toggle_subtask, delete_checklist',
    steps: [
      {
        prompt: `Create a task called "Subtask Test ${tag}" for today. Then add a checklist with items: "Step A", "Step B", "Step C". Do it all now without asking.`,
        expectedTools: ['create_task', 'create_subtasks'],
      },
      {
        prompt: `Show me the subtasks/checklist for "Subtask Test ${tag}".`,
        expectedTools: ['get_subtasks'],
      },
      {
        prompt: `Toggle the first subtask of "Subtask Test ${tag}" to mark it done.`,
        expectedTools: ['toggle_subtask'],
      },
      {
        prompt: `Delete the entire checklist from "Subtask Test ${tag}".`,
        expectedTools: ['delete_checklist'],
      },
      {
        prompt: `Mark "Subtask Test ${tag}" as completed to clean up.`,
        expectedTools: ['complete_task'],
      },
    ],
  }
}

function noteToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Note Tools',
    description: 'Tests create_note, find_note, update_note, delete_note',
    steps: [
      {
        prompt: `Create a note titled "Tool Test Note ${tag}" with content "This is a test note created by the tool test runner." Do not ask for confirmation.`,
        expectedTools: ['create_note'],
        expectedActions: ['created'],
        validate: (r) => {
          const created = r.toolResults.find(t => t.action === 'created' && t.entity_type === 'note')
          return created ? null : 'No note created event received'
        },
      },
      {
        prompt: `Find the note called "Tool Test Note ${tag}".`,
        expectedTools: ['find_note'],
      },
      {
        prompt: `Update the note "Tool Test Note ${tag}" - change its content to "Updated content by tool test."`,
        expectedTools: ['update_note'],
        expectedActions: ['updated'],
      },
      {
        prompt: `Delete the note called "Tool Test Note ${tag}".`,
        expectedTools: ['delete_note'],
        expectedActions: ['deleted'],
      },
    ],
  }
}

function memoryToolTests(): ToolTestSet {
  return {
    name: 'Memory Tools',
    description: 'Tests search_memory, read_context_item, write_context_item',
    steps: [
      {
        prompt: 'Read my user details from memory. Use the read_context_item tool with type "user_details".',
        expectedTools: ['read_context_item'],
      },
      {
        prompt: 'Search your memory for anything about my preferences or interests.',
        expectedTools: ['search_memory'],
      },
      {
        prompt: 'Read my diary from memory using read_context_item.',
        expectedTools: ['read_context_item'],
      },
    ],
  }
}

function projectToolTests(): ToolTestSet {
  return {
    name: 'Project Tools',
    description: 'Tests list_projects',
    steps: [
      {
        prompt: 'List all my projects.',
        expectedTools: ['list_projects'],
      },
    ],
  }
}

function followUpToolTests(): ToolTestSet {
  return {
    name: 'Follow-up Tools',
    description: 'Tests create_follow_up, list_follow_ups, modify_follow_up',
    steps: [
      {
        prompt: 'List my scheduled follow-ups.',
        expectedTools: ['list_follow_ups'],
      },
    ],
  }
}

function contactToolTests(): ToolTestSet {
  return {
    name: 'Contact Tools',
    description: 'Tests get_contacts',
    steps: [
      {
        prompt: 'Show me my contacts.',
        expectedTools: ['get_contacts'],
      },
    ],
  }
}

function bulletinToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Bulletin Tools',
    description: 'Tests create_bulletin_ticket, update_bulletin_ticket, close_bulletin_ticket',
    steps: [
      {
        prompt: `Create a bulletin ticket with headline "Test Bulletin ${tag}". Do it now without asking.`,
        expectedTools: ['create_bulletin_ticket'],
        expectedActions: ['created'],
      },
      {
        prompt: `Update the bulletin ticket "Test Bulletin ${tag}" - change follow-up to 3 days from now.`,
        expectedTools: ['update_bulletin_ticket'],
        expectedActions: ['updated'],
      },
      {
        prompt: `Close the bulletin ticket "Test Bulletin ${tag}" with resolution note "Closed by tool test".`,
        expectedTools: ['close_bulletin_ticket'],
        expectedActions: ['updated'],
      },
    ],
  }
}

function documentToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Document Generation',
    description: 'Tests create_document — generate HTML, Markdown, and text documents',
    steps: [
      {
        prompt: `Create a short HTML document titled "Test Report ${tag}" about testing best practices. Keep it brief — just 2-3 paragraphs. Use the create_document tool with format "html". Do not ask for confirmation.`,
        expectedTools: ['create_document'],
        expectedActions: ['created'],
        validate: (r) => {
          // Check for generated_content event
          const genEvent = r.allEvents.find(e => e.type === 'generated_content')
          if (genEvent) {
            console.log('  [info] generated_content event received')
          }
          // Check for note creation
          const created = r.toolResults.find(t => t.action === 'created' && t.entity_type === 'note')
          if (created) return null
          // If we got a text response, the tool likely ran silently
          return r.responseText.length > 0 ? null : 'No document created'
        },
      },
      {
        prompt: `Create a short Markdown document titled "Markdown Test ${tag}" with a heading, a bullet list, and a code block. Use the create_document tool with format "md". Do not ask for confirmation.`,
        expectedTools: ['create_document'],
        validate: (r) => r.responseText.length > 0 ? null : 'No response',
      },
      {
        prompt: `Delete the note called "Test Report ${tag}" to clean up.`,
        expectedTools: ['delete_note'],
      },
      {
        prompt: `Delete the note called "Markdown Test ${tag}" to clean up.`,
        expectedTools: ['delete_note'],
      },
    ],
  }
}

function messagingToolTests(): ToolTestSet {
  return {
    name: 'Messaging Tools',
    description: 'Tests get_messages and modify_message for inter-persona messages',
    steps: [
      {
        prompt: 'List my recent inter-persona messages. Use the get_messages tool.',
        expectedTools: ['get_messages'],
      },
      {
        prompt: 'Show me unread messages only — call get_messages with status filter "unread".',
        expectedTools: ['get_messages'],
      },
      {
        prompt: 'Mark all my unread messages as read using modify_message. If there are none, just confirm.',
        // modify_message may not run if there are no unread messages — accept text response too.
        expectedTools: ['modify_message'],
      },
    ],
  }
}

function emailToolTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Email Tools',
    description: 'Tests send_message with recipient_type="email" — composes an email (review-gated, not auto-sent)',
    steps: [
      {
        prompt: `Compose an email to test@example.com with subject "Tool Test Email ${tag}" and body "This is a test email composed by the tool test runner. Please ignore." Use the send_message tool with recipient_type "email" and to_email "test@example.com". Do not ask for confirmation.`,
        expectedTools: ['send_message'],
        // External emails go through a review handoff, so no "created" entity action is emitted.
        validate: (r) => {
          const sent = r.allEvents.find(e => {
            if (e.type !== 'cloud_tool_result') return false
            const d = e.data as Record<string, unknown> | undefined
            return d?.tool === 'send_message'
          })
          if (sent) return null
          return r.responseText.length > 0 ? null : 'No send_message result and no response'
        },
      },
      {
        prompt: 'List my recent inter-persona messages with get_messages so I can see the email I just composed.',
        expectedTools: ['get_messages'],
      },
    ],
  }
}

function imageGenerationTests(): ToolTestSet {
  const tag = `test_${Date.now()}`
  return {
    name: 'Image Generation',
    description: 'Tests create_image — generate AI images',
    steps: [
      {
        prompt: `Generate a simple image of a red circle on a white background. Name it "Test Image ${tag}". Use aspect ratio 1:1. Use the create_image tool. Do not ask for confirmation.`,
        expectedTools: ['create_image'],
        expectedActions: ['created'],
        validate: (r) => {
          const genEvent = r.allEvents.find(e => e.type === 'generated_content')
          if (genEvent) {
            console.log('  [info] generated_content event received for image')
          }
          const created = r.toolResults.find(t => t.action === 'created' && t.entity_type === 'note')
          if (created) return null
          return r.responseText.length > 0 ? null : 'No image created'
        },
      },
      {
        prompt: `Delete the note called "Test Image ${tag}" to clean up.`,
        expectedTools: ['delete_note'],
      },
    ],
  }
}

// ── Registry ───────────────────────────────────────────────────

const TEST_SET_FACTORIES: Record<string, () => ToolTestSet> = {
  'Task Tools': taskToolTests,
  'Subtask Tools': subtaskToolTests,
  'Note Tools': noteToolTests,
  'Memory Tools': memoryToolTests,
  'Project Tools': projectToolTests,
  'Follow-up Tools': followUpToolTests,
  'Contact Tools': contactToolTests,
  'Bulletin Tools': bulletinToolTests,
  'Messaging Tools': messagingToolTests,
  'Email Tools': emailToolTests,
  'Document Generation': documentToolTests,
  'Image Generation': imageGenerationTests,
}

export function getToolTestSets(): { name: string; description: string }[] {
  return Object.entries(TEST_SET_FACTORIES).map(([name, factory]) => {
    const set = factory()
    return { name, description: set.description }
  })
}

// ── Execution ──────────────────────────────────────────────────

async function collectResponse(
  voiceChatId: string,
  prompt: string,
  model: string,
): Promise<StepResult> {
  let responseText = ''
  const toolResults: CloudToolResult[] = []
  const allEvents: ChatEvent[] = []

  for await (const event of sendMessage(voiceChatId, prompt, model)) {
    allEvents.push(event)
    if (event.type === 'text' || event.type === 'content') {
      responseText += (typeof event.data === 'string' ? event.data : '') || ''
    } else if (event.type === 'cloud_tool_result' && event.data && typeof event.data === 'object') {
      toolResults.push(event.data as unknown as CloudToolResult)
    } else if (event.type === 'tool_progress' && event.data && typeof event.data === 'object') {
      const d = event.data as Record<string, unknown>
      console.log(`  [tool_progress] ${d.tool || d.name || 'unknown'}: ${d.status || 'executing'}`)
    } else if (event.type === 'error') {
      console.error(`  [stream_error] ${typeof event.data === 'string' ? event.data : JSON.stringify(event.data)}`)
    } else if (event.type === 'complete') {
      const d = event.data as Record<string, unknown> | undefined
      if (d) {
        console.log(`  [complete] cloud_tools_executed=${d.cloud_tools_executed}, tool_calls=${d.tool_calls}`)
      }
    } else {
      console.log(`  [event] type=${event.type}`)
    }
  }

  if (allEvents.length === 0) {
    console.warn('  [warning] Stream returned zero events')
  }

  return { responseText, toolResults, allEvents }
}

/** Validate a single step's result against expectations. Returns null on pass, error string on failure. */
function validateStep(step: ToolTestStep, result: StepResult): string | null {
  if (step.expectedTools && step.expectedTools.length > 0) {
    const triggeredTools = new Set<string>()
    for (const ev of result.allEvents) {
      if (ev.type === 'tool_progress' && ev.data && typeof ev.data === 'object') {
        const d = ev.data as Record<string, unknown>
        if (d.tool) triggeredTools.add(d.tool as string)
        if (d.name) triggeredTools.add(d.name as string)
      }
      if (ev.type === 'cloud_tool_result' && ev.data && typeof ev.data === 'object') {
        const d = ev.data as Record<string, unknown>
        if (d.tool) triggeredTools.add(d.tool as string)
      }
    }

    for (const expected of step.expectedTools) {
      if (!triggeredTools.has(expected)) {
        if (result.responseText.length > 0) {
          console.log(`  [info] Expected tool "${expected}" not in events — AI responded with text (tool likely executed silently)`)
        } else if (result.allEvents.length === 0) {
          return `Expected tool "${expected}" — stream returned zero events`
        } else {
          return `Expected tool "${expected}" — got ${result.allEvents.length} events but no text response`
        }
      }
    }
  }

  if (step.expectedActions && step.expectedActions.length > 0) {
    const gotActions = result.toolResults.map(t => t.action)
    for (const expected of step.expectedActions) {
      if (!gotActions.includes(expected as CloudToolResult['action'])) {
        console.log(`  [info] Expected action "${expected}" not in results: [${gotActions.join(', ')}]`)
      }
    }
  }

  if (step.validate) {
    const err = step.validate(result)
    if (err) return err
  }

  if (result.responseText.length === 0 && result.toolResults.length === 0) {
    return 'No response text and no tool results received'
  }

  return null
}

/** Run steps against an existing session. Used internally by both single-set and multi-set runners. */
async function runSteps(
  voiceChatId: string,
  setName: string,
  steps: ToolTestStep[],
  model: string,
  onProgress?: ToolTestProgressCallback,
): Promise<{ stepResults: ToolTestResult['steps']; allPassed: boolean }> {
  const stepResults: ToolTestResult['steps'] = []
  let allPassed = true

  for (let i = 0; i < steps.length; i++) {
    const step = steps[i]
    const stepStart = performance.now()
    onProgress?.(setName, i, steps.length, 'running', step.prompt)

    console.log(`\n--- [${setName}] Step ${i + 1}/${steps.length} ---`)
    console.log(`PROMPT: ${step.prompt}`)

    try {
      const result = await collectResponse(voiceChatId, step.prompt, model)
      const duration = performance.now() - stepStart

      console.log(`RESPONSE (${(duration / 1000).toFixed(1)}s): ${result.responseText.slice(0, 300)}${result.responseText.length > 300 ? '...' : ''}`)
      if (result.toolResults.length > 0) {
        console.log(`TOOL RESULTS:`, result.toolResults.map(t => `${t.tool}/${t.action}/${t.entity_type}`).join(', '))
      }

      const error = validateStep(step, result)
      const stepPassed = !error
      if (!stepPassed) allPassed = false

      console.log(`RESULT: ${stepPassed ? 'PASS' : `FAIL - ${error}`}`)

      stepResults.push({
        prompt: step.prompt,
        responseText: result.responseText,
        toolResults: result.toolResults,
        passed: stepPassed,
        error: error || undefined,
        duration,
      })

      onProgress?.(setName, i, steps.length, stepPassed ? 'passed' : 'failed', error || undefined)
    } catch (e) {
      const duration = performance.now() - stepStart
      const error = `Exception: ${e}`
      console.error(`RESULT: FAIL - ${error}`)
      allPassed = false

      stepResults.push({
        prompt: step.prompt,
        responseText: '',
        toolResults: [],
        passed: false,
        error,
        duration,
      })

      onProgress?.(setName, i, steps.length, 'failed', error)
    }
  }

  return { stepResults, allPassed }
}

/**
 * Run a single test set in its own session.
 * Used when clicking "Run" on an individual set.
 */
export async function runToolTestSet(
  setName: string,
  personaId: string,
  onProgress?: ToolTestProgressCallback,
  model = 'gemini-3-flash',
): Promise<ToolTestResult> {
  const factory = TEST_SET_FACTORIES[setName]
  if (!factory) {
    return { name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' }
  }

  const testSet = factory()
  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`TOOL TEST: ${setName}`)
  console.log(`Persona: ${personaId} | Model: ${model}`)
  console.log(`${'='.repeat(60)}`)

  let voiceChatId: string
  try {
    const startRes = await startChat({ personaId })
    voiceChatId = startRes.voice_chat_id
    console.log(`Session started: ${voiceChatId}`)
  } catch (e) {
    const error = `Failed to start session: ${e}`
    console.error(error)
    return { name: setName, passed: false, steps: [], duration: performance.now() - totalStart, error }
  }

  const { stepResults, allPassed } = await runSteps(voiceChatId, setName, testSet.steps, model, onProgress)

  try {
    await stopChat(voiceChatId, personaId)
    console.log(`\nSession stopped: ${voiceChatId}`)
  } catch (e) {
    console.warn(`Failed to stop session: ${e}`)
  }

  const totalDuration = performance.now() - totalStart
  console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(totalDuration / 1000).toFixed(1)}s)`)
  console.log('='.repeat(60))

  return { name: setName, passed: allPassed, steps: stepResults, duration: totalDuration }
}

/**
 * Run multiple test sets in a single shared chat session.
 * Simulates real-life usage where a user performs many different
 * operations within one conversation.
 */
export async function runAllToolTests(
  personaId: string,
  selectedSets: string[],
  onProgress?: ToolTestProgressCallback,
  model = 'gemini-3-flash',
): Promise<ToolTestResult[]> {
  const totalStart = performance.now()

  console.log(`\n${'='.repeat(60)}`)
  console.log(`TOOL TEST RUN: ${selectedSets.length} set(s) in a single session`)
  console.log(`Persona: ${personaId} | Model: ${model}`)
  console.log(`${'='.repeat(60)}`)

  // One session for the entire run
  let voiceChatId: string
  try {
    const startRes = await startChat({ personaId })
    voiceChatId = startRes.voice_chat_id
    console.log(`Session started: ${voiceChatId}`)
  } catch (e) {
    const error = `Failed to start session: ${e}`
    console.error(error)
    return selectedSets.map(name => ({
      name, passed: false, steps: [], duration: 0, error,
    }))
  }

  const results: ToolTestResult[] = []

  for (const setName of selectedSets) {
    const factory = TEST_SET_FACTORIES[setName]
    if (!factory) {
      results.push({ name: setName, passed: false, steps: [], duration: 0, error: 'Test set not found' })
      continue
    }

    const testSet = factory()
    const setStart = performance.now()

    console.log(`\n${'─'.repeat(40)}`)
    console.log(`SET: ${setName}`)
    console.log(`${'─'.repeat(40)}`)

    const { stepResults, allPassed } = await runSteps(voiceChatId, setName, testSet.steps, model, onProgress)

    const setDuration = performance.now() - setStart
    console.log(`\n${setName}: ${allPassed ? 'ALL PASSED' : 'SOME FAILED'} (${(setDuration / 1000).toFixed(1)}s)`)

    results.push({ name: setName, passed: allPassed, steps: stepResults, duration: setDuration })
  }

  // Stop the shared session
  try {
    await stopChat(voiceChatId, personaId)
    console.log(`\nSession stopped: ${voiceChatId}`)
  } catch (e) {
    console.warn(`Failed to stop session: ${e}`)
  }

  const totalDuration = performance.now() - totalStart
  const passed = results.filter(r => r.passed).length
  console.log(`\n${'='.repeat(60)}`)
  console.log(`ALL DONE: ${passed}/${results.length} sets passed (${(totalDuration / 1000).toFixed(1)}s total)`)
  console.log('='.repeat(60))

  return results
}
